import React, { useState, useRef, useContext } from 'react';
import { GatsbyImage, getImage } from 'gatsby-plugin-image';
import { renderRichText } from 'gatsby-source-contentful/rich-text';
import { ThemeContext } from 'styled-components';

import { Layout, BlueBox, Feature, AdaptiveArrow } from '@/components/general';
import { Breadcrumbs } from '@/components/general/Breadcrumbs';
import { Links } from '@/utils/constants';
import { Tag, TagAttachment, TagVariants, TemplateCard } from '../components';
import { options } from './Options';

import * as styled from './TemplateSlug.styled';

interface ITemplateSlug {
  data: any;
}

export const TemplateSlug: React.FC<ITemplateSlug> = ({ data }) => {
  const {
    title,
    technologies,
    languages,
    bootstrap,
    mainScreens,
    richText,
    features,
  } = data;

  const theme = useContext(ThemeContext);

  const mainText = renderRichText(richText, options);

  const [isScreenOpen, setIsScreenOpen] = useState(false);

  const MainScreensSection = useRef(null);

  const BOOTSTRAP = [
    {
      top: `${bootstrap[0]}+`,
      bot: 'ready screens*',
    },
    {
      top: `${bootstrap[1]}h`,
      bot: 'saved in time*',
    },
    {
      top: `$${bootstrap[2]}k`,
      bot: 'saved in budget*',
    },
  ];

  const TemplateBoxRef = useRef(null);

  return (
    <Layout>
      <Breadcrumbs links={[Links.TEMPLATES, title]} $hideOnLg />
      <AdaptiveArrow
        elRef={TemplateBoxRef}
        arrowVisibilityHeight={920}
        $boxPaddingTop={96}
        $boxPaddingTopMobile={32}
        nextSectionPadding={130}
        nextSectionPaddingMobile={70}
      >
        <styled.TemplateBox ref={TemplateBoxRef}>
          <TemplateCard template={data} isTemplateSlug />
        </styled.TemplateBox>
      </AdaptiveArrow>
      <styled.BootstrapBox>
        <styled.BootstrapTitle>Bootstrap your product 🚀</styled.BootstrapTitle>
        <styled.BootstrapRow>
          {BOOTSTRAP.map((el) => (
            <styled.BootstrapElement key={el.bot}>
              <styled.ElementTop>{el.top}</styled.ElementTop>
              <styled.ElementBottom>{el.bot}</styled.ElementBottom>
            </styled.BootstrapElement>
          ))}
        </styled.BootstrapRow>
      </styled.BootstrapBox>
      <styled.MainScreensSection>
        <styled.MainScreensColor>
          <styled.MainScreensBox
            $isOpen={isScreenOpen}
            $adaptiveHeight={
              MainScreensSection?.current?.getBoundingClientRect()?.height
            }
          >
            <styled.MainScreensShadow $exist={!isScreenOpen} />
            <styled.MainScreensTitle>Main Screens</styled.MainScreensTitle>
            <styled.MainSceensGrid ref={MainScreensSection}>
              {mainScreens.map(({ collection }) =>
                collection.map((screen, index) => (
                  <styled.ScreenImageBox key={`${screen.description}-${index}`}>
                    <GatsbyImage
                      image={getImage(screen.gatsbyImageData)}
                      alt={screen.description}
                    />
                  </styled.ScreenImageBox>
                )),
              )}
            </styled.MainSceensGrid>
          </styled.MainScreensBox>
        </styled.MainScreensColor>
        <styled.ButtonBox>
          <styled.ButtonSee
            onClick={
              !isScreenOpen
                ? () => setIsScreenOpen(true)
                : () => setIsScreenOpen(false)
            }
            color={theme.colors.black}
          >
            {!isScreenOpen ? 'See all' : 'Hide'}
          </styled.ButtonSee>
        </styled.ButtonBox>
      </styled.MainScreensSection>
      <styled.DescriptionSection>
        <styled.TextBox>{mainText}</styled.TextBox>
        <styled.FeaturesBox>
          <styled.CharacteristicBox>
            <styled.CharacteristicTitle>
              Technologies
            </styled.CharacteristicTitle>
            <styled.TagsBox>
              {technologies.map((technology) => (
                <Tag
                  key={technology}
                  variant={TagVariants.SMALL}
                  attachment={TagAttachment.TECHNOLOGIES}
                >
                  {technology}
                </Tag>
              ))}
            </styled.TagsBox>
          </styled.CharacteristicBox>
          <styled.CharacteristicBox>
            <styled.CharacteristicTitle>Languages</styled.CharacteristicTitle>
            <styled.TagsBox>
              {languages.map((language) => (
                <Tag
                  key={language}
                  variant={TagVariants.SMALL}
                  attachment={TagAttachment.LANGUAGES}
                >
                  {language}
                </Tag>
              ))}
            </styled.TagsBox>
          </styled.CharacteristicBox>
          <styled.CharacteristicBox>
            <styled.CharacteristicTitle>Features</styled.CharacteristicTitle>
            <styled.FeaturesList>
              {features.map((feature) => (
                <Feature
                  key={feature.feature}
                  question={feature.feature}
                  answer={feature.description.description}
                />
              ))}
            </styled.FeaturesList>
          </styled.CharacteristicBox>
        </styled.FeaturesBox>
      </styled.DescriptionSection>
      <BlueBox
        title="Didn't find the app template you want?"
        description="Talk to our experts for a quick consultation, template request, custom design or development"
        $hasCaption={false}
      />
    </Layout>
  );
};
